/* ====================================================================
   NEW ZEALAND PUBLIC HOLIDAYS

   Computed, not stored. A club should not have to type Labour Day into
   its calendar every year, and a table in the database would be one more
   thing to keep right for a set of dates that never change their rules.

   Two of them do not follow a rule you can write down as arithmetic:

   - Easter moves with the moon, so Good Friday and Easter Monday come
     out of the standard Gregorian computus below.
   - Matariki moves with the stars and the maramataka. See MATARIKI.

   The anniversary day depends on where the club is, so it comes from the
   club's own `anniversaryRegion` setting rather than from here.
   ==================================================================== */

const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const isWeekend = d => d.getDay() === 0 || d.getDay() === 6;

/**
 * Matariki.
 *
 * There is a rule, and it is not one this file can run. The holiday falls
 * on the Friday closest to the four nights of Tangaroa — the last-quarter
 * period — in the lunar month of Pipiri, the month in which the Matariki
 * star cluster rises before dawn in midwinter. That is a maramataka
 * calculation over a lunar month whose start is itself observed, not a
 * formula over the Gregorian year, and an approximation of it would be
 * wrong by a week in some years and right in others with no way to tell
 * which from the code.
 *
 * So the dates are taken from where they are actually decided: the
 * schedule to Te Ture mō te Hararei Tūmatanui o te Kāhui o Matariki /
 * Te Kāhui o Matariki Public Holiday Act 2022, which fixes all of them
 * from 2022 to 2052. Beyond 2052 there is no mark rather than a guess.
 */
const MATARIKI = {
  2022: '2022-06-24', 2023: '2023-07-14', 2024: '2024-06-28', 2025: '2025-06-20',
  2026: '2026-07-10', 2027: '2027-06-25', 2028: '2028-07-14', 2029: '2029-07-06',
  2030: '2030-06-21', 2031: '2031-07-11', 2032: '2032-07-02', 2033: '2033-06-24',
  2034: '2034-07-07', 2035: '2035-06-29', 2036: '2036-07-18', 2037: '2037-07-10',
  2038: '2038-06-25', 2039: '2039-07-15', 2040: '2040-07-06', 2041: '2041-07-19',
  2042: '2042-07-11', 2043: '2043-07-03', 2044: '2044-06-24', 2045: '2045-07-07',
  2046: '2046-06-29', 2047: '2047-07-19', 2048: '2048-07-03', 2049: '2049-06-25',
  2050: '2050-07-15', 2051: '2051-06-30', 2052: '2052-06-21',
};

/** Easter Sunday, anonymous Gregorian computus. */
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

/** The nth given weekday of a month — first Monday in June, and so on. */
function nthWeekday(year, month, weekday, n) {
  const d = new Date(year, month, 1);
  const shift = (weekday - d.getDay() + 7) % 7;
  return new Date(year, month, 1 + shift + (n - 1) * 7);
}

/**
 * The Monday nearest a date, which is how most anniversary days are set.
 *
 * Tuesday, Wednesday and Thursday look back to the Monday just gone;
 * Friday, Saturday and Sunday look forward to the one coming. A date that
 * is already a Monday stays put.
 */
function mondayNearest(year, month, day) {
  const d = new Date(year, month, day);
  const from = (d.getDay() + 6) % 7;                 // 0 = Monday
  return new Date(year, month, day + (from <= 3 ? -from : 7 - from));
}

const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/* ------------------------------------------------------------------
   REGIONAL ANNIVERSARY DAYS

   Every region keeps a nominal date it is the anniversary *of*, and
   observes it on a day that suits a working week — usually the nearest
   Monday, sometimes a day pinned to Labour Day or to Easter instead. The
   nominal dates are in the labels; the functions give the day off.

   Two are looser than a function can be. Westland's varies within the
   region by local custom, and Otago's has to give way when it lands on
   Easter. Both are noted where they are computed.
   ------------------------------------------------------------------ */
export const ANNIVERSARY_REGIONS = [
  { value: 'auckland', label: 'Auckland / Northland', holiday: 'Auckland Anniversary Day' },
  { value: 'taranaki', label: 'Taranaki', holiday: 'Taranaki Anniversary Day' },
  { value: 'hawkes-bay', label: "Hawke's Bay", holiday: "Hawke's Bay Anniversary Day" },
  { value: 'wellington', label: 'Wellington', holiday: 'Wellington Anniversary Day' },
  { value: 'marlborough', label: 'Marlborough', holiday: 'Marlborough Anniversary Day' },
  { value: 'nelson', label: 'Nelson', holiday: 'Nelson Anniversary Day' },
  { value: 'canterbury', label: 'Canterbury', holiday: 'Canterbury Anniversary Day (Show Day)' },
  { value: 'south-canterbury', label: 'South Canterbury', holiday: 'South Canterbury Anniversary Day (Dominion Day)' },
  { value: 'westland', label: 'Westland / West Coast', holiday: 'Westland Anniversary Day' },
  { value: 'otago', label: 'Otago', holiday: 'Otago Anniversary Day' },
  { value: 'southland', label: 'Southland', holiday: 'Southland Anniversary Day' },
  { value: 'chatham-islands', label: 'Chatham Islands', holiday: 'Chatham Islands Anniversary Day' },
];

export const ANNIVERSARY_REGION_VALUES = ANNIVERSARY_REGIONS.map(r => r.value);

const ANNIVERSARY_RULES = {
  wellington: year => mondayNearest(year, 0, 22),
  auckland: year => mondayNearest(year, 0, 29),
  nelson: year => mondayNearest(year, 1, 1),
  // Nominally 31 March, moved to the second Monday in March to clear Easter.
  taranaki: year => nthWeekday(year, 2, 1, 2),
  otago: (year, easter) => {
    const d = mondayNearest(year, 2, 23);
    /* Nominally 23 March, but Easter takes precedence: in a year where the
       nearest Monday is Easter Monday the province observes it on the
       Tuesday instead. */
    return iso(d) === iso(addDays(easter, 1)) ? addDays(easter, 2) : d;
  },
  // Nominally 17 January; observed on Easter Tuesday since 2011.
  southland: (year, easter) => addDays(easter, 2),
  // Dominion Day, the fourth Monday in September.
  'south-canterbury': year => nthWeekday(year, 8, 1, 4),
  // Nominally 1 November; the Friday before Labour Day.
  'hawkes-bay': year => addDays(nthWeekday(year, 9, 1, 4), -3),
  // Nominally 1 November; the first Monday after Labour Day.
  marlborough: year => addDays(nthWeekday(year, 9, 1, 4), 7),
  // Show Day: the second Friday after the first Tuesday in November.
  canterbury: year => addDays(nthWeekday(year, 10, 2, 1), 10),
  // Nominally 30 November.
  'chatham-islands': year => mondayNearest(year, 10, 30),
  /* Nominally 1 December. Observance varies around the coast by local
     custom; the Monday nearest is what most of Westland keeps, and a club
     that keeps another day can ignore the mark. */
  westland: year => mondayNearest(year, 11, 1),
};

/**
 * The region's anniversary day for a year, or null if no region is set.
 * `easter` is passed in so the two Easter-bound regions do not each
 * recompute the computus.
 */
export function anniversaryDay(year, region, easter = easterSunday(year)) {
  const rule = ANNIVERSARY_RULES[region];
  if (!rule) return null;
  const meta = ANNIVERSARY_REGIONS.find(r => r.value === region);
  return { name: meta.holiday, date: rule(year, easter) };
}

/**
 * The year's holidays, with weekend ones moved as the Holidays Act moves
 * them: a holiday that falls on a Saturday or Sunday is observed on the
 * next working day that is not already a public holiday.
 *
 * That one rule covers every case people remember as special. When
 * Christmas Day is a Sunday it does not simply become Monday, because
 * Boxing Day is already sitting there — it moves past it to Tuesday. The
 * same thing happens to New Year's Day when the 2nd is the Monday.
 * Processing the weekday-dated holidays first, and the weekend ones in
 * date order after them, produces that without a single special case.
 */
export function nzHolidays(year, region = null) {
  const matariki = MATARIKI[year];
  const easter = easterSunday(year);
  const anniversary = anniversaryDay(year, region, easter);

  const fixed = [
    { name: "New Year's Day", date: new Date(year, 0, 1), moves: true },
    { name: "Day after New Year's Day", date: new Date(year, 0, 2), moves: true },
    { name: 'Waitangi Day', date: new Date(year, 1, 6), moves: true },
    { name: 'Good Friday', date: new Date(easter.getFullYear(), easter.getMonth(), easter.getDate() - 2), moves: false },
    { name: 'Easter Monday', date: new Date(easter.getFullYear(), easter.getMonth(), easter.getDate() + 1), moves: false },
    { name: 'ANZAC Day', date: new Date(year, 3, 25), moves: true },
    // Renamed on the accession, so the year decides which name is right.
    { name: year >= 2023 ? "King's Birthday" : "Queen's Birthday", date: nthWeekday(year, 5, 1, 1), moves: false },
    ...(matariki ? [{ name: 'Matariki', date: new Date(`${matariki}T00:00:00`), moves: false }] : []),
    /* Anniversary days are already set to a working day by their own rule,
       so they never mondayise — and they sort into place with the rest
       rather than being appended, since the sort at the end is by date. */
    ...(anniversary ? [{ name: anniversary.name, date: anniversary.date, moves: false }] : []),
    { name: 'Labour Day', date: nthWeekday(year, 9, 1, 4), moves: false },
    { name: 'Christmas Day', date: new Date(year, 11, 25), moves: true },
    { name: 'Boxing Day', date: new Date(year, 11, 26), moves: true },
  ];

  /* Anything already on a weekday holds its date, and holds it against
     the ones being moved. */
  const taken = new Set(
    fixed.filter(h => !h.moves || !isWeekend(h.date)).map(h => iso(h.date)),
  );

  const out = [];
  fixed.forEach(h => {
    if (!h.moves || !isWeekend(h.date)) {
      out.push({ date: iso(h.date), name: h.name, observedFor: null });
      return;
    }
    const d = new Date(h.date);
    // At most a week of walking: a free weekday always turns up long before.
    for (let i = 0; i < 10; i++) {
      d.setDate(d.getDate() + 1);
      if (!isWeekend(d) && !taken.has(iso(d))) break;
    }
    taken.add(iso(d));
    /* Both days are marked. The 25th is still Christmas Day to everyone
       looking at the calendar, and the Tuesday is still the day the club
       is shut, so a chart that showed only one of them would be wrong
       for half the people reading it. */
    out.push({ date: iso(h.date), name: h.name, observedFor: null });
    out.push({ date: iso(d), name: `${h.name} (observed)`, observedFor: h.name });
  });

  return out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/**
 * ISO date -> holiday name, over a span of years.
 *
 * Where two land on one day — Boxing Day and an observed Christmas — the
 * names are joined, because the day is both and the cell has room for
 * one line.
 */
export function nzHolidayMap(fromYear, toYear, region = null) {
  const map = new Map();
  const last = Math.min(toYear, fromYear + 5);   // a planner spans two at most
  for (let y = fromYear; y <= last; y++) {
    nzHolidays(y, region).forEach(h => {
      map.set(h.date, map.has(h.date) ? `${map.get(h.date)}, ${h.name}` : h.name);
    });
  }
  return map;
}

export { easterSunday, nthWeekday, MATARIKI };
